interface Breakpoint {
  name: BreakpointName;
  width: number;
}

type BreakpointName = 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL' | 'XXXL' | 'fullHD';

export const breakpoints: Breakpoint[] = [
  {
    name: 'XS',
    width: 360,
  },
  {
    name: 'S',
    width: 560,
  },
  {
    name: 'M',
    width: 760,
  },
  {
    name: 'L',
    width: 960,
  },
  {
    name: 'XL',
    width: 1160,
  },
  {
    name: 'XXL',
    width: 1360,
  },
  {
    name: 'XXXL',
    width: 1660,
  },
  {
    name: 'fullHD',
    width: 1920,
  },
];

interface IPubSub {
  e: Record<string, Event>;
  emit: (name: string) => void;
  on: (name: string, handler: EventListenerOrEventListenerObject) => void;
  off: (name: string, handler: EventListenerOrEventListenerObject) => void;
}

const pubsub = {} as IPubSub;

/*! Js Pub/Sub
 * http://anasnakawa.com/
 * Copyright (c) Anas Nakawa
 * inspired by Ben Alman's one <https://gist.github.com/cowboy/661855>
 * MIT License
 */
(function (p: IPubSub) {
  const e: Record<string, Event> = (p.e = {});

  p.emit = (name: string) => {
    const event = (e[name] = e[name] || new Event(name)) as Event;
    dispatchEvent(event);
  };

  p.on = (name: string, handler: EventListenerOrEventListenerObject) => {
    addEventListener(name, handler);
  };

  p.off = (name: string, handler: EventListenerOrEventListenerObject) => {
    removeEventListener(name, handler);
  };
})(pubsub);

const emitChangeWithDebounced = (() => {
  let timer: number;
  return () => {
    clearTimeout(timer);
    timer = window.setTimeout(() => pubsub.emit('change:width'), 100);
  };
})();

type BreakpointMap = Record<BreakpointName, number>;
type BreakpointFlagUp = `${BreakpointName}_and_up`;
type BreakpointFlagDown = `up_to_${BreakpointName}`;
type BreakpointFlagName = BreakpointFlagUp | BreakpointFlagDown;
type BreakpointFlags = Record<BreakpointFlagName, boolean>;

class MediaQuery {
  breakpoints: BreakpointMap;
  on: (name: string, handler: EventListenerOrEventListenerObject) => void;
  off: (name: string, handler: EventListenerOrEventListenerObject) => void;

  widths: BreakpointFlags;

  constructor() {
    this.breakpoints = breakpoints.reduce((agg, {name, width}) => {
      agg[name] = width;
      return agg;
    }, {} as BreakpointMap);

    this.on = pubsub.on;
    this.off = pubsub.off;

    this.widths = breakpoints.reduce(
      (agg: Partial<BreakpointFlags>, {name, width}) => {
        this.addXAndUp(name, width, agg);
        this.addUpToX(name, width, agg);
        return agg;
      },
      {} as Partial<BreakpointFlags>,
    ) as BreakpointFlags;
  }

  addAndWatchFlag(
    name: BreakpointName,
    flagName: BreakpointFlagName,
    mediaQueryString: string,
    agg: Partial<BreakpointFlags>,
  ) {
    const mediaQuery: MediaQueryList = window.matchMedia(mediaQueryString);
    agg[flagName] = mediaQuery.matches;

    const listener = ({matches}: MediaQueryListEvent) => {
      this.widths[flagName] = matches;
      pubsub.emit(`change:${name}`);
      emitChangeWithDebounced();
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
    } else {
      mediaQuery.addListener(listener);
    }

    pubsub.emit(`change:${name}`);
    emitChangeWithDebounced();
  }

  addXAndUp(
    name: BreakpointName,
    width: number,
    agg: Partial<BreakpointFlags>,
  ) {
    const flagName: BreakpointFlagUp = `${name}_and_up`;
    const mediaQueryString = `(min-width: ${width}px)`;
    this.addAndWatchFlag(name, flagName, mediaQueryString, agg);
  }

  private addUpToX(
    name: BreakpointName,
    width: number,
    agg: Partial<BreakpointFlags>,
  ) {
    const flagName: BreakpointFlagDown = `up_to_${name}`;
    const mediaQueryString = `(max-width: ${width - 1}px)`;
    this.addAndWatchFlag(name, flagName, mediaQueryString, agg);
  }

  prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

export const mediaQuery = new MediaQuery();
